// src/features/admin/dashboardApi.ts
import { createApi, fetchBaseQuery } from '@reduxjs/toolkit/query/react';

export type RangeType =
  | 'today'
  | 'yesterday'
  | 'this_week'
  | 'last_week'
  | 'this_month'
  | 'last_month'
  | 'this_quarter'
  | 'last_quarter'
  | 'this_year'
  | 'last_year'
  | 'custom';

// Types for Most Viewed Products API (from backend)
export interface MostViewedProduct {
  product_id: number;
  product_name: string;
  price: string;
  total_views: number;
  total_viewers: number;
}

export interface MostViewedResponse {
  error: boolean;
  total: number;
  data: MostViewedProduct[];
  message?: string;
}

// Types for High Sale Products API (from backend)
export interface HighSaleProduct {
  product_id: number;
  product_name: string;
  price: string;
  total_qty: number;
  total_buyers: number;
  avg_sale_per_day: number;
}

export interface HighSaleResponse {
  error: boolean;
  total: number;
  data: HighSaleProduct[];
  message?: string;
}

// Dashboard types remain the same
export interface DashboardAnalyticsResponse {
  success: boolean;
  daily_sales: any;
  weekly_sales: any;
  monthly_sales: any;
  top_products: any[];
  new_users: any;
  month_growth: any;
  quarter_growth: any;
  range: any;
}

export const dashboardApi = createApi({
  reducerPath: 'dashboardApi',
  baseQuery: fetchBaseQuery({
    baseUrl: 'https://fftestservice.sarsetech.com',
    prepareHeaders: (headers) => {
      const token = localStorage.getItem('admin_token');
      if (token) {
        headers.set('Authorization', `Bearer ${token}`);
      }
      headers.set('authkey', '7b4e1fd03f57885c1ff900ad76ea3810bb7160c7157a4c31cf2de3');
      headers.set('Content-Type', 'application/json');
      return headers;
    },
  }),
  endpoints: (build) => ({
    getDashboardAnalytics: build.query<DashboardAnalyticsResponse, RangeType>({
      query: (rangeType) => ({
        url: '/admin/dashboard-analytics',
        method: 'POST',
        body: { range_type: rangeType },
      }),
    }),
    
    getMostViewedProducts: build.query<MostViewedResponse, {
      range: 'week' | 'month' | 'year' | 'custom';
      from_date?: string;
      to_date?: string;
      pdf?: 0 | 1;
      csv?: 0 | 1;
    }>({
      query: (params) => ({
        url: '/admin/most-viewed-products',
        method: 'POST',
        body: params,
      }),
    }),
    
    getHighSaleProducts: build.query<HighSaleResponse, {
      range: 'week' | 'month' | 'year' | 'custom';
      from_date?: string;
      to_date?: string;
      pdf?: 0 | 1;
      csv?: 0 | 1;
    }>({
      query: (params) => ({
        url: '/admin/high-sale-products',
        method: 'POST',
        body: params,
      }),
    }),
    
    downloadMostViewedPDF: build.mutation<{error: boolean; message: string; download_url: string}, {
      range: 'week' | 'month' | 'year' | 'custom';
      from_date?: string;
      to_date?: string;
    }>({
      query: (params) => ({
        url: '/admin/most-viewed-products',
        method: 'POST',
        body: { ...params, pdf: 1, csv: 0 },
      }),
    }),
    
    downloadHighSalePDF: build.mutation<{error: boolean; message: string; download_url: string}, {
      range: 'week' | 'month' | 'year' | 'custom';
      from_date?: string;
      to_date?: string;
    }>({
      query: (params) => ({
        url: '/admin/high-sale-products',
        method: 'POST',
        body: { ...params, pdf: 1, csv: 0 },
      }),
    }),
    
    downloadMostViewedCSV: build.mutation<Blob, {
      range: 'week' | 'month' | 'year' | 'custom';
      from_date?: string;
      to_date?: string;
    }>({
      query: (params) => ({
        url: '/admin/most-viewed-products',
        method: 'POST',
        body: { ...params, csv: 1, pdf: 0 },
        responseHandler: async (response) => {
          if (response.ok) {
            return await response.blob();
          }
          throw new Error('CSV download failed');
        },
      }),
    }),
    
    downloadHighSaleCSV: build.mutation<Blob, {
      range: 'week' | 'month' | 'year' | 'custom';
      from_date?: string;
      to_date?: string;
    }>({
      query: (params) => ({
        url: '/admin/high-sale-products',
        method: 'POST',
        body: { ...params, csv: 1, pdf: 0 },
        responseHandler: async (response) => {
          if (response.ok) {
            return await response.blob();
          }
          throw new Error('CSV download failed');
        },
      }),
    }),
  }),
});

export const { 
  useGetDashboardAnalyticsQuery, 
  useGetMostViewedProductsQuery,
  useGetHighSaleProductsQuery,
  useDownloadMostViewedPDFMutation,
  useDownloadHighSalePDFMutation,
  useDownloadMostViewedCSVMutation,
  useDownloadHighSaleCSVMutation,
} = dashboardApi;